import React from 'react';

/**
 * Platform Icon Component
 *
 * Renders official platform logos following brand guidelines.
 * YouTube branding follows: https://developers.google.com/youtube/terms/branding-guidelines
 */
const PlatformIcon = ({ platform, size = 'md', className = '', variant = 'color' }) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  // YouTube icon minimum visible height is 20dp per branding guidelines
  // viewBox is cropped to icon bounds (0 3.5 24 17) but SVG path doesn't fill full viewBox,
  // so we need h-[21px] (not h-5/20px) to ensure the rendered path is >= 20px
  const ytSizeClasses = {
    xs: 'h-[21px] w-auto',
    sm: 'h-[21px] w-auto',
    md: 'h-[21px] w-auto',
    lg: 'h-6 w-auto',
    xl: 'h-8 w-auto'
  };

  const platformLower = platform?.toLowerCase();
  const sizeClass = platformLower === 'youtube'
    ? (ytSizeClasses[size] || ytSizeClasses.md)
    : (sizeClasses[size] || sizeClasses.md);

  switch (platformLower) {
    case 'youtube': {
      // Official YouTube logo following branding guidelines
      // Minimum 20dp, with accessible name per https://developers.google.com/youtube/terms/branding-guidelines
      // Allowed color combinations: "Red and White" or "Black and White"
      // Use variant="white" on colored/dark backgrounds, variant="dark" for black icon
      const ytFill = variant === 'white' ? '#FFFFFF' : variant === 'dark' ? '#000000' : '#FF0000';
      return (
        <svg viewBox="0 3.5 24 17" fill={ytFill} className={`${sizeClass} ${className}`} role="img" aria-label="YouTube">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    }

    case 'twitch':
      return (
        <svg viewBox="0 0 24 24" fill="#9146FF" className={`${sizeClass} ${className}`}>
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
        </svg>
      );

    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" fill="#000000" className={`${sizeClass} ${className}`}>
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      );

    case 'twitter':
      return (
        <svg viewBox="0 0 24 24" fill="#000000" className={`${sizeClass} ${className}`}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );

    case 'instagram':
      return (
        <svg viewBox="0 0 24 24" fill="#E4405F" className={`${sizeClass} ${className}`}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      );

    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" fill="#1877F2" className={`${sizeClass} ${className}`}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );

    case 'discord':
      return (
        <svg viewBox="0 0 24 24" fill="#5865F2" className={`${sizeClass} ${className}`}>
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      );

    case 'kick':
      return (
        <svg viewBox="0 0 24 24" fill="#00E70A" className={`${sizeClass} ${className}`}>
          <path d="M18.5 8.5L13 14l5.5 5.5L16 22l-8-8 8-8z"/>
          <path d="M2 2h4v20H2z"/>
        </svg>
      );

    default:
      return (
        <div className={`${sizeClass} ${className} bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold`}>
          {platform ? platform.charAt(0).toUpperCase() : '?'}
        </div>
      );
  }
};

export default PlatformIcon;
